import { z } from "zod";
import { GenderRuleSchema, ListingAmenitySchema, ListingTypeSchema, PropertyStatusSchema, PropertyTypeSchema, RoomTypeSchema } from "./listings";

/**
 * The landlord's side: their own listings and the requests on them.
 *
 * These replies DO contain the street address and postal code, because they are
 * the owner's own data shown back to the owner. Every endpoint checks ownership
 * and answers 404 for a listing that belongs to someone else.
 */

const iso = z.iso.datetime();

export const MyPropertySchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  city: z.string(),
  province: z.string(),
  status: PropertyStatusSchema,
  totalSlots: z.number().int(),
  availableSlots: z.number().int(),
  /** Bare bed price of every room, in cents. */
  roomPrices: z.array(z.number().int()),
});
export const MyPropertiesSchema = z.object({
  items: z.array(MyPropertySchema),
  /** How many live listings this landlord's plan allows. */
  propertyLimit: z.number().int(),
});
export type MyPropertiesDto = z.infer<typeof MyPropertiesSchema>;

/** Everything the edit-listing page needs about one of the landlord's own listings. */
export const MyPropertyDetailSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  propertyType: PropertyTypeSchema,
  listingType: ListingTypeSchema,
  status: PropertyStatusSchema,
  addressLine1: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string(),
  province: z.string(),
  country: z.string(),
  cityId: z.uuid().nullable(),
  verificationStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]),
  slaPausedAt: iso.nullable(),
  images: z.array(z.object({ id: z.uuid(), url: z.string(), isCover: z.boolean(), category: z.string() })),
  videos: z.array(z.object({ id: z.uuid(), url: z.string(), mimeType: z.string(), durationSec: z.number().int().nullable() })),
  amenities: z.array(ListingAmenitySchema),
  rooms: z.array(
    z.object({
      id: z.uuid(),
      label: z.string(),
      capacity: z.number().int(),
      pricePerSlot: z.number().int(),
      roomType: RoomTypeSchema,
      genderRule: GenderRuleSchema,
      furnished: z.boolean(),
      /** Beds free right now. */
      available: z.number().int(),
      /** Beds occupied or reserved. */
      occupied: z.number().int(),
      /** Live requests or approvals on this room. A room with any cannot be deleted. */
      activeBookings: z.number().int(),
    }),
  ),
});
export type MyPropertyDetail = z.infer<typeof MyPropertyDetailSchema>;

/** A request or inquiry on one of the landlord's listings. Never carries the tenant's contact details. */
export const IncomingRequestSchema = z.object({
  id: z.uuid(),
  status: z.enum(["REQUESTED", "APPROVED", "REJECTED", "CANCELLED", "EXPIRED", "COMPLETED"]),
  isInquiry: z.boolean(),
  message: z.string().nullable(),
  moveInDate: iso.nullable(),
  requestedAt: iso,
  respondedAt: iso.nullable(),
  responseDueAt: iso.nullable(),
  property: z.object({ title: z.string(), slug: z.string() }),
  roomLabel: z.string(),
  tenantName: z.string().nullable(),
  /** Has the landlord already reviewed this tenant? */
  reviewed: z.boolean(),
});
export const IncomingRequestsSchema = z.object({ items: z.array(IncomingRequestSchema) });
export type IncomingRequestDto = z.infer<typeof IncomingRequestSchema>;
